<script setup>
/** One policy as a card, up to four of them (prototype `polCard`). Opens the policy. */
import { computed } from 'vue';
import { RouterLink } from 'vue-router';
import CoverageArt from './CoverageArt.vue';
import CoverageRing from './CoverageRing.vue';
import { money, numberLine, policyTitle, renewal, ringFor } from '../../helpers/coverage';

const props = defineProps({
  policy: { type: Object, required: true },
  now: { type: Date, required: true },
  // Home (XN-4734): how far away the renewal is, in place of "Tap for the details".
  detail: { type: Boolean, default: false },
});
const when = computed(() => renewal(props.policy, props.now));
const dial = computed(() => ringFor(when.value));
const premium = computed(() => {
  const amount = money(props.policy.monthly_premium, props.policy.currency);

  return amount ? `${amount}/mo` : '';
});
const hint = computed(() => (props.detail ? when.value.detail : 'Tap for the details'));
</script>

<template>
  <RouterLink :to="`/hub/coverage/${policy.id}`" class="cov-card" data-testid="policy-card">
    <CoverageArt :line="policy.line" />
    <span class="cov-card__bd">
      <b class="cov-card__title">{{ policyTitle(policy) }}</b>
      <span class="cov-card__num">{{ numberLine(policy) }}</span>
      <span v-if="premium" class="cov-card__num" data-testid="card-premium">{{ premium }}</span>
      <span class="cov-card__foot">
        <CoverageRing v-if="dial" :percent="dial.percent" :label="dial.label" :soon="when.soon" />
        <span class="cov-card__fl">
          <b :class="{ 'cov-card__late': when.state === 'overdue' }">{{ when.headline }}</b>
          <span v-if="hint" class="cov-card__hint">{{ hint }}</span>
        </span>
      </span>
    </span>
  </RouterLink>
</template>

<style scoped>
.cov-card {
  display: block; border-radius: var(--xl-radius-lg); background: #fff; border: 1px solid var(--xl-color-border);
  overflow: hidden; box-shadow: var(--xl-shadow-1); transition: transform .18s, box-shadow .18s, border-color .18s;
  color: inherit; text-decoration: none;
}
.cov-card:hover { transform: translateY(-2px); box-shadow: var(--xl-shadow-2); border-color: var(--xl-color-primary-fixed-dim); }
.cov-card:focus-visible { outline: 2px solid var(--xl-color-primary); outline-offset: 2px; }
.cov-card__bd { display: block; padding: 16px 18px 18px; }
.cov-card__title { display: block; font-size: 16.5px; letter-spacing: -0.015em; overflow-wrap: anywhere; }
.cov-card__num { display: block; margin-top: 2px; font-size: 13px; color: var(--xl-color-outline); overflow-wrap: anywhere; }
.cov-card__foot { display: flex; align-items: center; gap: 10px; margin-top: 14px; }
.cov-card__fl { flex: 1; font-size: 13px; line-height: 1.35; }
.cov-card__fl b { font-size: 13.5px; letter-spacing: 0; display: block; }
.cov-card__late { color: var(--xl-color-warning); }
.cov-card__hint { display: block; color: var(--xl-color-outline); }
</style>
